import { RouteProp } from '@react-navigation/native';
import React, { useEffect, useMemo, useState } from 'react';
import { ProfilesTabParamList, useProfilesTabNavigation } from '../../../../../utils/appNavigationNames';
import useSetHeaderTitle from '../../../../../hooks/useSetHeaderTitle';
import { pluralResourceCategoryMessages } from '../../../../../utils/sharedMessages';
import { getIntl } from '../../../../../utils/intl';
import NotesList from '../../../../shared/Notes/NotesList';
import useNotes from '../../../../../hooks/api/useNotes';
import { useEnvironment } from '../../../../../hooks/useEnvironment';
import StandardResourceList from './StandardResourceList';
import LabDateBucketList from './LabDateBucketList';
import UniqueLabs from './UniqueLabs';
import { TabView, TabOption } from '../../../../v2BaseComponents/TabView';
import ProfileHeaderContainer from '../../../../v2BaseComponents/ProfileHeaderContainer';
import useMedicalProfileData from '../../../../../hooks/api/useMedicalProfileData';
import { SearchFilterValue } from '../../../../shared/ResourceSearch/ResourceListSearchFilters';
import { getTabLabelSummary } from '../../../../shared/ResourceSearch/ResourceListSearchFilters/getFilterSummary';

const intl = getIntl('medical-data-list-screen', {
    header: `{category}`,
    add: 'Add {category}',
    dateAdded: 'Date added',
    status: 'Status',
    source: 'Source',
    ehrIntegration: 'Patient Portal',
    primaryRecord: 'Primary Record',
    resources: 'List',
    notes: 'Notes',
    active: 'Active',
    inactive: 'Inactive',
    calendar: 'Calendar',
    labDates: 'Lab Dates',
});

type Tab = 'resources' | 'notes' | 'calendar' | 'panels';

export type DataScreenOwnProps = {};

export type DataScreenProps = DataScreenOwnProps & {
    route: RouteProp<ProfilesTabParamList, 'data'>;
};

type SearchFilterStateAndCount = {
    searchFilterState: SearchFilterValue | null;
    count: number;
};

const DataScreen: React.FC<DataScreenProps> = (props) => {
    const navigation = useProfilesTabNavigation();
    const environment = useEnvironment();
    const { category, profileId } = props.route.params;
    const isCalendarEnabled = environment === 'dev' && category === 'visit' && false;
    const [tab, setTab] = useState<Tab>(isCalendarEnabled ? 'calendar' : 'resources');
    useEffect(() => {
        setTab(isCalendarEnabled ? 'calendar' : 'resources');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [category]);
    const noteQueryArgs = useMemo(
        () => ({ medicalProfileId: profileId, entityType: category, entityId: profileId }),
        [category, profileId],
    );
    const { notes } = useNotes({
        noteQueryArgs,
    });
    const { groupedResources, isLoadingResources } = useMedicalProfileData({
        medicalProfileId: profileId,
        resourceCategory: category,
    });
    const [searchFilterState, setSearchFilterState] = useState<SearchFilterStateAndCount | null>(null);
    const listLabel = useMemo(() => {
        if (!searchFilterState?.searchFilterState) {
            return intl('resources', {
                count: isLoadingResources ? '...' : groupedResources?.length || 0,
            });
        }

        return getTabLabelSummary({
            value: searchFilterState.searchFilterState,
            category,
            displayedCount: searchFilterState.count,
            isLoading: isLoadingResources,
        });
    }, [searchFilterState, category, isLoadingResources, groupedResources?.length]);

    const tabOptions: TabOption[] = useMemo(
        () => [
            {
                title: listLabel,
                value: 'resources',
                renderTabContent: () =>
                    category === 'lab' ? (
                        <UniqueLabs medicalProfileId={profileId} />
                    ) : (
                        <StandardResourceList
                            resourceCategory={category}
                            medicalProfileId={profileId}
                            onSearchFilterUpdate={(filter, count) =>
                                setSearchFilterState({ searchFilterState: filter, count })
                            }
                        />
                    ),
            },
            {
                title: intl('labDates'),
                value: 'panels',
                hide: category !== 'lab',
                renderTabContent: () => <LabDateBucketList medicalProfileId={profileId} />,
            },
            {
                title: intl('notes') + ` (${notes?.notes?.length || 0})`,
                value: 'notes',
                renderTabContent: () => (
                    <NotesList
                        linkEmptyStateHint
                        noteQueryArgs={noteQueryArgs}
                        onAddNote={(args) => {
                            navigation.navigate('add-note', args);
                        }}
                        onViewDetails={(note) => {
                            navigation.navigate('note', {
                                noteId: note.noteId,
                                noteEntityType: note.entityType,
                                profileId: profileId,
                            });
                        }}
                    />
                ),
            },
        ],
        [category, listLabel, navigation, noteQueryArgs, notes?.notes?.length, profileId],
    );

    useEffect(() => {
        if (!tabOptions.find((t) => t.value === tab)) {
            setTab(tabOptions[0].value as Tab);
        }
    }, [category, tabOptions, tab]);

    useSetHeaderTitle(
        intl('header', {
            category: pluralResourceCategoryMessages(category),
        }),
    );

    return (
        <ProfileHeaderContainer profileId={profileId}>
            <TabView key={category} tabs={tabOptions} selectedTab={tab} onChange={(tab) => setTab(tab as Tab)} />
        </ProfileHeaderContainer>
    );
};

export default React.memo(DataScreen);
